import { useEffect, useState } from "react";
import Chessboard from "../Chessboard";
import { Chess } from "chess.js";
import { Exclusion, Position } from "../models";
import useSound from "use-sound";
import moveSelfSound from "./../assets/sound-effects/move-self.mp3";
import { getLichessOpeningData, LichessResponse } from "../getOpeningBookMove";
import { fetchPositions } from "../positions/fetchPositions";
import _ from "lodash";
import { useExclusionsStorage, useStorage } from "../ZustandState";
import useKeypress from "react-use-keypress";
import { useForceUpdate } from "@mantine/hooks";
import Header from "../Header";
import { useQuery } from "react-query";
import { getUser, redirectToPremiumIfNotPremium } from "../Authentication";
import { INITIAL_POSITION } from "../utils";
import ExclusionsSidePanel from "./ExclusionsSidePanel.tsx";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

export const fetchExlusions = async (positionId: number): Promise<Exclusion[]> => {
    if (!positionId) {
        console.log("Missing positionId");
        // return;
    }
    const exclusions = await axios.get(
        `${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_exclusions?positionId=${positionId}`,
        {
            withCredentials: true,
        }
    );

    const data = exclusions.data;

    return data;
};

const Exclusions = () => {
    const [game, setGame] = useState<Chess>();
    const [selectedPosition, setSelectedPosition] = useState<Position>();
    const [playMoveSelfSound] = useSound(moveSelfSound);
    const { data: positions } = useQuery(["positions"], () => fetchPositions());
    const [openingData, setOpeningData] = useState<LichessResponse>();
    const setExcludedMoves = useExclusionsStorage((state) => state.setExcludedMoves);
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: exclusions, isLoading } = useQuery(["exclusions"], () => fetchExlusions(selectedPosition?.id!), {
        enabled: !!selectedPosition,
    });
    const forceUpdate = useForceUpdate();

    const playerColor = useStorage((state) => state.playerColor);
    const [redoStack, setRedoStack] = useState<string[]>([]);

    const { data: user, isLoading: isLoadingUser } = useQuery(["user"], () => getUser());

    redirectToPremiumIfNotPremium(user);

    const fetchAndSetOpeningData = async (newGame: Chess, position: Position) => {
        const openingResponse = await getLichessOpeningData(newGame, [`${position?.rating!}`]);
        setOpeningData(openingResponse);
    };

    useEffect(() => {
        if (!positions) {
            return;
        }

        setSelectedPosition(INITIAL_POSITION);

        const positionId = searchParams.get("position") || undefined;
        const position = positions?.find((position) => position.id === parseInt(positionId || ""));
        if (position) {
            setSelectedPosition(position);
            const game = new Chess();
            position.pgn ? game.loadPgn(position.pgn) : game.load(position.fen);
            setGame(game);
            fetchAndSetOpeningData(game, position);
        }
    }, [positions]);

    useEffect(() => {
        if (game && selectedPosition && exclusions) {
            const alreadyExcludedMoves = exclusions
                ?.filter((exclusion) => exclusion.fen === game?.fen())
                .map((exclusion) => exclusion.move);

            setExcludedMoves(alreadyExcludedMoves || []);
        }
    }, [selectedPosition, exclusions]);

    useKeypress(["ArrowLeft", "ArrowRight"], (event: any) => {
        if (event.key === "ArrowLeft") {
            const lastMove = _.last(game?.history());
            if (lastMove) {
                setRedoStack([...redoStack, lastMove]);
                game?.undo();
                handleOnMove();
            }
        } else {
            const lastMove = _.last(redoStack);
            if (lastMove) {
                game?.move(lastMove);
                setRedoStack(redoStack.slice(0, redoStack.length - 1));
                handleOnMove();
            }
        }
    });

    if (!selectedPosition) {
        return;
    }

    const handleOnMove = async (move?: any, shouldPlaySound = true) => {
        if (!game) {
            return;
        }

        if (shouldPlaySound) {
            playMoveSelfSound();
        }

        await fetchAndSetOpeningData(game, selectedPosition);

        const alreadyExcludedMoves = exclusions
            ?.filter((exclusion) => exclusion.fen === game?.fen())
            .map((exclusion) => exclusion.move);
        setExcludedMoves(alreadyExcludedMoves || []);
    };

    if (!game) {
        return;
    }

    return (
        <div>
            <Header user={user} selected="positions" />
            <div className="flex justify-center max-[1023px]:flex-col max-[md]:justify-center max-[md]:space-y-6 [@media(max-width:1023px)]:items-center">
                <Chessboard
                    game={game}
                    onMove={handleOnMove}
                    playerColor={playerColor}
                    result={undefined}
                    state="positions"
                />
                <ExclusionsSidePanel
                    openingData={openingData}
                    isPlayersTurn={game.turn() === selectedPosition.defaultPlayerColor}
                    selectedPosition={selectedPosition}
                    game={game}
                    exclusions={exclusions}
                    onLoadFen={(fen) => {
                        game.load(fen);
                        forceUpdate();
                        handleOnMove(undefined, false);
                    }}
                />
            </div>
        </div>
    );
};

export default Exclusions;
